const SCORE_META = {
  score: {
    label: 'Gesamt',
    className: 'total',
    fallbackInfo: 'Gesamtbewertung aus Machbarkeit, Profilnähe, Strategie und harten Anforderungen.',
  },
  machbarkeit_score: {
    label: 'Machbarkeit',
    className: 'machbarkeit',
    fallbackInfo: 'Ob die Tätigkeit realistisch ausgeführt oder schnell gelernt werden kann.',
  },
  profilnaehe_score: {
    label: 'Profilnähe',
    className: 'profilnaehe',
    fallbackInfo: 'Wie nah die Stelle am bisherigen Profil und Erfahrungsfeld liegt.',
  },
  strategie_score: {
    label: 'Strategie',
    className: 'strategie',
    fallbackInfo: 'Wie glaubwürdig Motivation, Verfügbarkeit und Wechselgrund erklärbar sind.',
  },
};

function scoreToTen(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const score = n > 10 ? n / 10 : n;
  return Math.max(0, Math.min(10, Math.round(score * 10) / 10));
}

function scoreToPercent(value) {
  const score = scoreToTen(value);
  return score == null ? null : Math.round(score * 10);
}

function formatScore(score) {
  if (score == null) return '–';
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

export function fitScoreColor(score) {
  const normalized = Math.max(0, Math.min(10, Number(score) || 0)) / 10;
  const hue = Math.round(4 + normalized * 126);
  return `hsl(${hue} 72% 38%)`;
}

export function fitScoreGradient(score) {
  const normalized = Math.max(0, Math.min(10, Number(score) || 0)) / 10;
  const hue = Math.round(4 + normalized * 126);
  return `linear-gradient(90deg, hsl(${hue} 82% 58%), hsl(${hue} 72% 38%))`;
}

export function scoreTone(percent) {
  if (percent == null) return 'neutral';
  if (percent >= 80) return 'strong';
  if (percent >= 60) return 'solid';
  if (percent >= 45) return 'mixed';
  return 'weak';
}

export function buildFitScores(analyseStruktur, fallbackPercent = null) {
  const source = analyseStruktur || {};
  const explanations = source.score_erklaerungen || {};
  const overallPercent = scoreToPercent(source.score) ?? fallbackPercent;

  const entries = Object.entries(SCORE_META)
    .map(([key, meta]) => {
      const raw = key === 'score' ? (source.score ?? (fallbackPercent != null ? fallbackPercent / 10 : null)) : source[key];
      const percent = scoreToPercent(raw);
      if (percent == null && key !== 'score') return null;
      const value = key === 'score' && percent == null ? overallPercent : percent;
      if (value == null) return null;
      const score = percent == null && key === 'score'
        ? scoreToTen(value)
        : scoreToTen(raw);
      return {
        key,
        label: meta.label,
        className: meta.className,
        percent: value,
        score,
        display: `${formatScore(score)}/10`,
        color: fitScoreColor(score),
        gradient: fitScoreGradient(score),
        tone: scoreTone(value),
        info: explanations[key] || meta.fallbackInfo,
      };
    })
    .filter(Boolean);

  if (entries.length === 0 && fallbackPercent != null) {
    entries.push({
      key: 'score',
      label: SCORE_META.score.label,
      className: SCORE_META.score.className,
      percent: fallbackPercent,
      score: scoreToTen(fallbackPercent),
      display: `${formatScore(scoreToTen(fallbackPercent))}/10`,
      color: fitScoreColor(scoreToTen(fallbackPercent)),
      gradient: fitScoreGradient(scoreToTen(fallbackPercent)),
      tone: scoreTone(fallbackPercent),
      info: SCORE_META.score.fallbackInfo,
    });
  }

  return entries;
}

export function fitScoreLabel(analyseStruktur, fallback = '') {
  return analyseStruktur?.score_label || fallback;
}

/**
 * Gibt ein qualitatives Urteil für einen 0–10-Score zurück.
 * @param {number|null} score  0–10
 * @param {{ neutral?: boolean }} opts  neutral=true → kein Alarm-Rot (z.B. für Profilnähe)
 */
export function scoreQualitative(score, opts = {}) {
  const s = Math.max(0, Math.min(10, Number(score) || 0));
  if (s >= 7) return { level: 'hoch', label: 'Hoch', color: opts.neutral ? 'var(--cv-teal, #004D40)' : fitScoreColor(s) };
  if (s >= 4) return { level: 'mittel', label: 'Mittel', color: opts.neutral ? 'var(--cv-text-muted, #6B7280)' : fitScoreColor(s) };
  return { level: 'gering', label: 'Gering', color: opts.neutral ? 'var(--cv-text-muted, #6B7280)' : fitScoreColor(s) };
}

/**
 * Baut das kompakte Urteil-Objekt für die neue Fit-Analyse-Ansicht.
 * Baut auf buildFitScores auf, ohne es zu ersetzen.
 *
 * @returns {{
 *   label: string,
 *   overall: object|null,
 *   dimensions: Array<{label:string,key:string,level:string,levelLabel:string,color:string,info:string}>,
 *   detailRows: Array,
 * }}
 */
export function buildFitVerdict(analyseStruktur, fallbackPercent = null) {
  const source = analyseStruktur || {};
  const detailRows = buildFitScores(source, fallbackPercent);
  const label = fitScoreLabel(source);
  const overall = detailRows.find(r => r.key === 'score') || detailRows[0] || null;
  const explanations = source.score_erklaerungen || {};

  const dimensionKeys = [
    { key: 'machbarkeit_score', label: 'Machbarkeit', neutral: false },
    { key: 'profilnaehe_score', label: 'Profilnähe', neutral: true },
  ];

  const dimensions = dimensionKeys.map(({ key, label: dimLabel, neutral }) => {
    const row = detailRows.find(r => r.key === key);
    const qual = scoreQualitative(row?.score ?? null, { neutral });
    return {
      key,
      label: dimLabel,
      level: qual.level,
      levelLabel: qual.label,
      color: qual.color,
      info: explanations[key] || row?.info || '',
      available: row != null,
    };
  }).filter(d => d.available);

  return { label, overall, dimensions, detailRows };
}
